import { useEffect, useState } from "react";
import { Modal, Form, Input, Select, InputNumber, DatePicker, Row, Col, Divider, Typography } from "antd";
import { createProducto, updateProducto } from "../../../../services/financiero/finProductosService";
import { getProveedores } from "../../../../services/financiero/finProveedoresService";
import { getEstados } from "../../../../services/financiero/finEstadoService";
import { getUnidadesMedida } from "../../../../services/financiero/finUnidadMedidaService";

const { Title } = Typography;

export default function ProductoForm({ open, onClose, onSuccess, producto, userLogin }) {

  const [form] = Form.useForm();
  const [proveedores, setProveedores] = useState([]);
  const [estados, setEstados] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const estadoMap = {
    "FINALLY CLOSED": "Entregado",
    "CLOSED": "Cerrado",
    "CLOSED FOR RECEIVING": "Cerrado para recepción",
    "OPEN": "Pendiente",
    "PENDING": "Pendiente"
  };
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    cargarCatalogos();
  }, []);

  useEffect(() => {
    if (producto) {
      form.setFieldsValue(producto);
    } else {
      form.resetFields();
    }
  }, [producto]);

  const cargarCatalogos = async () => {
    try {
      const prov = await getProveedores();
      const est = await getEstados();
      const uni = await getUnidadesMedida();
      setProveedores(prov);
      setEstados(est);
      setUnidades(uni);
    } catch (err) {
      console.error(err);
    }
  }

  const handleSubmit = async (values) => {
    try {
      values.fecha_estimada_entrega =
        values.fecha_estimada_entrega?.format("YYYY-MM-DD");
      if (producto) {
        await updateProducto(producto.oid, values, userLogin);
      } else {
        await createProducto(values, userLogin);
      }
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error(error);
    }
  }

  const checkForm = () => {
    const values = form.getFieldsValue();
    const requiredFields = [
      "Numero_PO",
      "nombre_producto",
      "cantidad",
      "oid_proveedor"
    ];
    const valid = requiredFields.every(field => values[field]);
    setIsValid(valid);
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      okButtonProps={{ disabled: !isValid }}
      width={1100}
      title={producto ? "Editar producto" : "Registrar nuevo producto"}
      okText="Guardar"
      cancelText="Cancelar"
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={checkForm}
      >
        <Title level={5}>Información del producto</Title>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="Número PO"
              name="Numero_PO"
              rules={[
                { required: true, message: "Ingrese el número PO" }
              ]}
            >
              <Input
                placeholder="Ej: 2312055"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Cantidad"
              name="cantidad"
              rules={[{ required: true }]}
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Cantidad del producto"
                min={0}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Unidad de medida"
              name="oid_unidad_medida"
            >
              <Select
                showSearch
                placeholder="Seleccione unidad"
                optionFilterProp="children"
              >
                {unidades.map(u => (
                  <Select.Option
                    key={u.oid}
                    value={u.oid}
                  >
                    {u.unidad_medida}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              label="Tipo de producto"
              name="tipo_producto"
            >
              <Input placeholder="Ej: Fertilizantes, maquinaria, servicios..." />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              label="Nombre del producto"
              name="nombre_producto"
              rules={[
                { required: true, message: "Ingrese el nombre del producto" }
              ]}
            >
              <Input
                placeholder="Ej: Fertilizante agrícola NPK"
              />
            </Form.Item>
          </Col>
        </Row>
        <Divider />
        <Title level={5}>Información financiera</Title>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Valor unitario COP"
              name="valor_unitario_COP"
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Valor en pesos colombianos"
                min={0}
                formatter={value =>
                  `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Valor unitario USD"
              name="valor_unitario_USD"
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Valor en dólares"
                min={0}
              />
            </Form.Item>
          </Col>
        </Row>
        <Divider />
        <Title level={5}>Proveedor y estado</Title>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Proveedor"
              name="oid_proveedor"
              rules={[{ required: true }]}
            >
              <Select
                showSearch
                placeholder="Seleccione proveedor"
                optionFilterProp="children"
              >
                {proveedores.map(p => (
                  <Select.Option
                    key={p.oid}
                    value={p.oid}
                  >
                    {p.nombre}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Estado"
              name="oid_estado"
            >
              <Select
                showSearch
                placeholder="Seleccione estado"
                optionFilterProp="children"
              >
                {estados.map(e => {
                  const estadoVisual = estadoMap[e.estado] || e.estado;
                  return (
                    <Select.Option
                      key={e.oid}
                      value={e.oid}
                    >
                      {estadoVisual}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Divider />
        <Title level={5}>Logística</Title>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Localización"
              name="localizacion"
            >
              <Input
                placeholder="Ej: Bogotá - Bodega principal"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Fecha estimada de entrega"
              name="fecha_estimada_entrega"
            >
              <DatePicker
                style={{ width: "100%" }}
                placeholder="Seleccione fecha"
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}